import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalanceWallet as WalletIcon, Add as AddIcon } from '@mui/icons-material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { useAccountsBase } from '../../components/expense/voucherShared';
import WalletLedger, { WalletBalances } from '../../components/expense/WalletLedger';
import { formatMoney, newRequestId, walletApi } from '../../services/expenseAccountsApi';

const MAX_COMMENT = 500;

// Add Balance: currency, amount (> 0) and a mandatory comment. One requestId per dialog
// opening, so a double click never adds the balance twice (the server dedupes it).
export const AddBalanceDialog = ({ open, employee, currencies, onClose, onAdded }) => {
  const [form, setForm] = useState({ currency: '', amount: '', comment: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState(newRequestId());

  useEffect(() => {
    if (open) {
      setForm({ currency: '', amount: '', comment: '' });
      setErrors({});
      setRequestId(newRequestId());
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await walletApi.addBalance(employee._id, {
        currency: form.currency,
        amount: form.amount === '' ? undefined : Number(form.amount),
        comment: form.comment,
        requestId,
      });
      toast.success(res.message);
      onAdded(res.data);
    } catch (e) {
      setErrors(e.errors || {});
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const comment = form.comment.trim();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add balance{employee ? ` - ${employee.firstName || ''} ${employee.lastName || ''}` : ''}</DialogTitle>
      <DialogContent>
        <TextField select fullWidth margin="dense" label="Currency" required value={form.currency}
          onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
          error={Boolean(errors.currency)} helperText={errors.currency}>
          {currencies.filter((c) => c.raw?.active !== false).map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <TextField fullWidth margin="dense" type="number" label="Amount" required value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          inputProps={{ min: 0, 'aria-label': 'Amount' }}
          error={Boolean(errors.amount)} helperText={errors.amount} />
        <TextField fullWidth margin="dense" multiline minRows={2} label="Comment" required value={form.comment}
          onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
          inputProps={{ 'aria-label': 'Comment' }}
          error={Boolean(errors.comment) || comment.length > MAX_COMMENT}
          helperText={errors.comment || `${comment.length}/${MAX_COMMENT}`} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submit}
          disabled={busy || !form.currency || form.amount === '' || !comment || comment.length > MAX_COMMENT}
          sx={{ textTransform: 'none' }}>
          Add balance
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Accounts wallet management: employees with a wallet, one employee's balances and ledger,
// and Add Balance for any active employee.
const AccountsWallets = () => {
  const accountsBase = useAccountsBase();
  const { users, currencies } = useExpenseLookups(['users', 'currencies']);
  const [list, setList] = useState([]);
  const [listError, setListError] = useState(null);
  const [selected, setSelected] = useState(null); // user object
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currency, setCurrency] = useState('');
  const [adding, setAdding] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setList((await walletApi.list({ limit: 100 })).data || []);
      setListError(null);
    } catch (e) {
      setListError(e);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadWallet = useCallback(async () => {
    if (!selected) return;
    try {
      setData((await walletApi.get(selected._id, { currency, page: page + 1, limit: rowsPerPage })).data);
    } catch (e) {
      toast.error(e.message);
    }
  }, [selected, currency, page, rowsPerPage]);
  useEffect(() => { loadWallet(); }, [loadWallet]);

  const select = (user) => {
    setSelected(user);
    setData(null);
    setPage(0);
    setCurrency('');
  };

  const userOptions = users.map((u) => u.raw);

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <WalletIcon color="primary" />
        Expense Wallets
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
        <Autocomplete
          sx={{ minWidth: 320 }}
          options={userOptions}
          value={selected}
          onChange={(e, u) => select(u)}
          getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''} (${u.email})`.trim()}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          renderInput={(params) => <TextField {...params} size="small" label="Employee" />}
        />
        {selected && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ textTransform: 'none' }}>Add balance</Button>
        )}
      </Box>

      {selected ? (
        data ? (
          <>
            <WalletBalances balances={data.balances} />
            <WalletLedger
              data={data}
              page={page}
              rowsPerPage={rowsPerPage}
              onPage={setPage}
              onRowsPerPage={(n) => { setRowsPerPage(n); setPage(0); }}
              currency={currency}
              onCurrency={(c) => { setCurrency(c); setPage(0); }}
              voucherLink={(e) => `${accountsBase}/${e.voucher}`}
            />
          </>
        ) : <CircularProgress />
      ) : (
        <>
          {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError.message}</Alert>}
          <Typography variant="h6" sx={{ mb: 1 }}>Employees with a wallet</Typography>
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Employee', 'Department', 'Balances'].map((h) => <TableCell key={h} sx={{ color: 'white', fontWeight: 600 }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary">No wallets yet</Typography></TableCell></TableRow>
                )}
                {list.map((w) => (
                  <TableRow key={w.employee?._id} hover sx={{ cursor: 'pointer' }} onClick={() => select(w.employee)}>
                    <TableCell>{`${w.employee?.firstName || ''} ${w.employee?.lastName || ''}`.trim()} ({w.employee?.email})</TableCell>
                    <TableCell>{w.employee?.department || '-'}</TableCell>
                    <TableCell>
                      {w.balances.map((b) => (
                        <Typography key={b.currency} component="span" sx={{ mr: 2, fontWeight: 600, color: b.balance < 0 ? 'error.main' : undefined }}>
                          {formatMoney(b.balance, b.currency)}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <AddBalanceDialog
        open={adding}
        employee={selected}
        currencies={currencies}
        onClose={() => setAdding(false)}
        onAdded={() => { setAdding(false); loadWallet(); loadList(); }}
      />
    </Box>
  );
};

export default AccountsWallets;
